// One place where a Google Places result is turned into our address fields.
//
// Every autocomplete in the app used to inline its own extraction, and they all made the same
// two assumptions:
//
//   1. `types[0] === "locality"` — matching only the FIRST entry of a component's `types`
//      array. Google documents `types` as an unordered set; the ordering is not part of the
//      contract, and a component whose first entry happens to be `political` simply never
//      matched.
//   2. City == `locality`. That holds in the US, but Google does not return a `locality` for
//      large parts of the world: the UK and Ireland use `postal_town`, NYC boroughs and many
//      Israeli neighbourhoods surface as `sublocality` / `sublocality_level_1`, and some
//      countries only carry `administrative_area_level_2`.
//
// Together those are why an autocomplete pick filled in the street and left City (and
// sometimes State/ZIP) blank. `component()` matches anywhere in `types`, and the city/state/zip
// getters walk an ordered fallback chain, taking the first type the result actually has.

export interface PlaceComponent {
  long_name: string;
  short_name?: string;
  types: string[];
}

export interface PlaceLike {
  address_components?: PlaceComponent[];
}

/**
 * The `long_name` of the first component carrying `type` anywhere in its `types`, or "".
 */
export const component = (place: PlaceLike | null | undefined, type: string): string => {
  const found = (place?.address_components ?? []).find((c) => (c?.types ?? []).includes(type));
  return found?.long_name ?? "";
};

/** As {@see component}, but returns `short_name` (falling back to `long_name`). */
export const componentShort = (place: PlaceLike | null | undefined, type: string): string => {
  const found = (place?.address_components ?? []).find((c) => (c?.types ?? []).includes(type));
  return found?.short_name || found?.long_name || "";
};

/** The first of `types` the place actually carries. */
const firstOf = (place: PlaceLike | null | undefined, types: string[]): string => {
  for (const type of types) {
    const value = component(place, type);
    if (value) return value;
  }
  return "";
};

// Most specific first: a result that has both a locality and a postal_town should use the
// locality. `sublocality_level_1` before `sublocality` because Google emits both on the same
// component and the level-1 name is the one people recognise (e.g. "Brooklyn").
export const CITY_TYPES = [
  "locality",
  "postal_town",
  "sublocality_level_1",
  "sublocality",
  "administrative_area_level_3",
  "administrative_area_level_2",
];

export const STATE_TYPES = [
  "administrative_area_level_1",
  // Some city-states and territories carry no level 1 at all.
  "administrative_area_level_2",
];

export const ZIP_TYPES = ["postal_code", "postal_code_prefix"];

export const cityFrom = (place: PlaceLike | null | undefined): string =>
  firstOf(place, CITY_TYPES);

export const stateFrom = (place: PlaceLike | null | undefined): string =>
  firstOf(place, STATE_TYPES);

export const zipFrom = (place: PlaceLike | null | undefined): string =>
  firstOf(place, ZIP_TYPES);

export const streetNameFrom = (place: PlaceLike | null | undefined): string =>
  component(place, "route");

export const streetNumberFrom = (place: PlaceLike | null | undefined): string =>
  component(place, "street_number");

export const subpremiseFrom = (place: PlaceLike | null | undefined): string =>
  component(place, "subpremise");

export const countryFrom = (place: PlaceLike | null | undefined): string =>
  component(place, "country");

/** The country's ISO-2 code (Google's `short_name` for the "country" component). */
export const countryIsoFrom = (place: PlaceLike | null | undefined): string =>
  componentShort(place, "country");

/**
 * The country name in CANONICAL ENGLISH, whatever language Google answered in.
 *
 * Google localises `long_name`, so the same place comes back as "United States"
 * or "ארצות הברית" depending on the request language — and the app loads Maps in
 * the user's language. A Hebrew-locale Shliach picking a Brooklyn address had
 * "ארצות הברית" stored as his country, which maps to no ISO code, which leaves
 * the workspace country-less. The spec's own words for that state: "a
 * country-less result would seed a 'problematic' region that route optimization
 * cannot place." It is already in live data — three accounts carry
 * `shlichus_country` = "Israel (‫ישראל‬‎)", bidi marks and all.
 *
 * `short_name` is the ISO-3166 alpha-2 code and is NOT localised, so it is the
 * only trustworthy thing in the response. Intl.DisplayNames turns it back into
 * the canonical English name the rest of the stack matches on (COUNTRIES here,
 * AddressService::countryNameToIso and FleetService::normalizeCountryCode on the
 * backend).
 *
 * Falls back to the localised name only when there is no ISO code at all, or the
 * runtime has no Intl.DisplayNames — a localised name is still better than an
 * empty one, and this must never throw inside an address pick.
 */
export const countryEnglishFrom = (place: PlaceLike | null | undefined): string => {
  const iso = countryIsoFrom(place);
  if (!iso) return countryFrom(place);
  try {
    const DisplayNames = (Intl as any)?.DisplayNames;
    if (!DisplayNames) return countryFrom(place) || iso;
    const name = new DisplayNames(["en"], { type: "region" }).of(iso.toUpperCase());
    // `.of()` echoes the input back when it does not recognise the code.
    return name && name !== iso.toUpperCase() ? name : countryFrom(place) || iso;
  } catch {
    return countryFrom(place) || iso;
  }
};

/**
 * The full set of address fields a Google pick populates, in the shape the forms store.
 * `unit_number` carries the street NUMBER — see project convention: the house number is
 * stored in `unit_number`, never `apartment_number`.
 */
export interface ExtractedAddressFields {
  unit_number: string;
  apartment_number: string;
  street_name: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
}

export const extractAddressFields = (
  place: PlaceLike | null | undefined
): ExtractedAddressFields => ({
  unit_number: streetNumberFrom(place),
  apartment_number: subpremiseFrom(place),
  street_name: streetNameFrom(place),
  city: cityFrom(place),
  state: stateFrom(place),
  country: countryEnglishFrom(place),
  zip_code: zipFrom(place),
});
